import React, { useState } from 'react';

export function InvestmentTheme({ title, subtitle, tickers, marketData, theme }) {
  const getTickerData = (ticker) => {
    if (!marketData?.allTickers) return null;
    return marketData.allTickers.find(t => t.ticker === ticker);
  };

  const items = tickers.map(ticker => {
    const data = getTickerData(ticker);
    return {
      ticker,
      name: data?.name || ticker,
      price: data?.ult ?? null,
      change: data?.var ?? null,
      weekly: data?.sem ?? null,
      monthly: data?.mes ?? null,
      yearly: data?.m12 ?? null,
      volume: data?.vol ?? null,
      hasData: !!data
    };
  }).filter(item => item.hasData);

  // Equal-weight basket performance
  const basketDaily = items.length > 0
    ? items.reduce((sum, i) => sum + (i.change ?? 0), 0) / items.length
    : 0;
  const basketYearly = items.length > 0
    ? items.reduce((sum, i) => sum + (i.yearly ?? 0), 0) / items.length
    : 0;

  const dailyPositive = basketDaily >= 0;
  const yearlyPositive = basketYearly >= 0;

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: '16px',
      border: `1px solid ${theme.border}`,
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'row',
    }}>
      {/* Left accent bar — gradient based on basket daily performance */}
      <div style={{
        width: '5px',
        flexShrink: 0,
        background: dailyPositive
          ? 'linear-gradient(to bottom, #4ade80, #166534)'
          : 'linear-gradient(to bottom, #ef4444, #7f1d1d)',
      }} />

      <div style={{ flex: 1, padding: '1.25rem 1.5rem' }}>
        {/* ── Card Header: Title + Basket Stats ── */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
          flexWrap: 'wrap',
          marginBottom: '0.75rem',
        }}>
          {/* Title block */}
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <h3 style={{
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: '#D4AF37',
              marginBottom: '0.2rem',
              lineHeight: 1.3,
            }}>
              {title}
            </h3>
            <p style={{
              fontSize: '0.8rem',
              color: theme.textSecondary,
              lineHeight: 1.4,
              margin: 0,
            }}>
              {subtitle}
            </p>
          </div>

          {/* Basket performance pills */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            flexShrink: 0,
            alignItems: 'center',
          }}>
            <PerformancePill label="Dia" value={basketDaily} theme={theme} />
            <PerformancePill label="12M" value={basketYearly} theme={theme} />
          </div>
        </div>

        {/* ── Ticker Grid ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
          gap: '0.5rem',
        }}>
          {items.map((item, i) => (
            <TickerCard key={i} item={item} theme={theme} />
          ))}
        </div>

        {/* ── Footer: asset count ── */}
        <div style={{
          marginTop: '0.75rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ fontSize: '0.7rem', color: theme.textSecondary }}>
            {items.length} ativos • Equal-weight
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Performance Pill  (used in card header)
───────────────────────────────────────────── */
function PerformancePill({ label, value, theme }) {
  const positive = value >= 0;
  const bg = positive ? 'rgba(74, 222, 128, 0.12)' : 'rgba(239, 68, 68, 0.12)';
  const border = positive ? 'rgba(74, 222, 128, 0.3)' : 'rgba(239, 68, 68, 0.3)';
  const color = positive ? '#4ade80' : '#ef4444';

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: bg,
      border: `1px solid ${border}`,
      borderRadius: '10px',
      padding: '0.4rem 0.75rem',
      minWidth: '68px',
    }}>
      <span style={{ fontSize: '0.6rem', color: theme.textSecondary, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
        {label}
      </span>
      <span style={{ fontSize: '1rem', fontWeight: 700, color, lineHeight: 1.2 }}>
        {positive ? '+' : ''}{value.toFixed(2)}%
      </span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Individual Ticker Card
───────────────────────────────────────────── */
function TickerCard({ item, theme }) {
  const [hovered, setHovered] = useState(false);
  const positive = (item.change ?? 0) >= 0;

  // Micro sparkline-style bar showing daily change magnitude
  const barWidth = Math.min(Math.abs(item.change ?? 0) * 8, 100);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        background: hovered
          ? (theme.itemBgHover || 'rgba(255,255,255,0.08)')
          : (theme.itemBg || 'rgba(255,255,255,0.03)'),
        borderRadius: '10px',
        padding: '0.7rem 0.75rem',
        cursor: 'pointer',
        transition: 'background 0.2s, transform 0.2s',
        transform: hovered ? 'translateY(-1px)' : 'none',
        overflow: 'hidden',
      }}
    >
      {/* Background bar showing magnitude */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        height: '3px',
        width: `${barWidth}%`,
        background: positive
          ? 'rgba(74, 222, 128, 0.35)'
          : 'rgba(239, 68, 68, 0.35)',
        borderRadius: '0 0 10px 10px',
        transition: 'width 0.4s ease',
      }} />

      {/* Top row: ticker + daily change */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.25rem',
      }}>
        <span style={{
          fontWeight: 700,
          fontSize: '0.85rem',
          color: theme.textPrimary,
          letterSpacing: '0.3px',
        }}>
          {item.ticker}
        </span>
        <span style={{
          fontSize: '0.8rem',
          fontWeight: 700,
          color: positive ? '#4ade80' : '#ef4444',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {positive ? '+' : ''}{(item.change ?? 0).toFixed(2)}%
        </span>
      </div>

      {/* Bottom row: name + price */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span style={{
          fontSize: '0.65rem',
          color: theme.textSecondary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '60%',
        }}>
          {item.name}
        </span>
        <span style={{
          fontSize: '0.72rem',
          color: theme.textSecondary,
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
        }}>
          R$ {item.price != null ? item.price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
        </span>
      </div>

      {/* Hover tooltip: extended metrics */}
      {hovered && (
        <div style={{
          marginTop: '0.5rem',
          paddingTop: '0.4rem',
          borderTop: `1px solid ${theme.border}`,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '0.25rem',
        }}>
          <MiniMetric label="Sem" value={item.weekly} theme={theme} />
          <MiniMetric label="Mês" value={item.monthly} theme={theme} />
          <MiniMetric label="12M" value={item.yearly} theme={theme} />
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Mini Metric (hover detail)
───────────────────────────────────────────── */
function MiniMetric({ label, value, theme }) {
  if (value == null) return null;
  const positive = value >= 0;
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '0.55rem', color: theme.textSecondary, textTransform: 'uppercase' }}>{label}</div>
      <div style={{
        fontSize: '0.7rem',
        fontWeight: 600,
        color: positive ? '#4ade80' : '#ef4444',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {positive ? '+' : ''}{value.toFixed(1)}%
      </div>
    </div>
  );
}
